import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Fade,
  IconButton,
  InputBase,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import {
  extractAmazonProductId,
  isAmazonUrl,
} from "../../../utils/amazonUrlParser";

import { navTokens } from "./navTokens";

interface SearchBarProps {
  variant?: "desktop" | "mobile" | "compact";
  fullWidth?: boolean;
}

const RETAILERS = {
  amazon: { label: "Amazon", logo: "/logos/amazon-logo.png" },
  // walmart: { label: "Walmart", logo: "/logos/walmart-logo.png" },
} as const;

type RetailerKey = keyof typeof RETAILERS;

const SearchBar: React.FC<SearchBarProps> = ({
  fullWidth = false,
  variant = "desktop",
}) => {
  const { t: myTranslate } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);

  const isExplicitMobile = variant === "mobile";
  const isCompact = variant === "compact";

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRetailer, setSelectedRetailer] =
    useState<RetailerKey>("amazon");
  const [isFocused, setIsFocused] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: "",
  });

  const isActive = isFocused || isMenuOpen;
  const trimmedQuery = searchQuery.trim();
  const retailerLabel = RETAILERS[selectedRetailer].label;

  // Sync search bar with URL params when on search page
  useEffect(() => {
    if (location.pathname !== "/search") {
      return;
    }
    const params = new URLSearchParams(location.search);
    const queryParam = params.get("query");
    const retailerParam = params.get("retailer");

    if (queryParam !== null) {
      setSearchQuery((prev) => (prev === queryParam ? prev : queryParam));
    }

    if (retailerParam) {
      const normalized = retailerParam.toLowerCase();
      if (normalized in RETAILERS) {
        setSelectedRetailer(normalized as RetailerKey);
      }
    }
  }, [location.pathname, location.search]);

  const handleRetailerChange = (event: SelectChangeEvent<unknown>) => {
    setSelectedRetailer(event.target.value as RetailerKey);
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!trimmedQuery) return;

    if (selectedRetailer === "amazon" && isAmazonUrl(trimmedQuery)) {
      const urlInfo = extractAmazonProductId(trimmedQuery);
      if (!urlInfo) {
        setSnackbar({
          open: true,
          message: myTranslate("navbar.shortUrlNotSupported"),
        });
        setSearchQuery("");
        inputRef.current?.focus();
        return;
      }
    }

    navigate(
      `/search?query=${encodeURIComponent(trimmedQuery)}&retailer=${selectedRetailer}&page=1`,
    );
  };

  const handleClear = () => {
    setSearchQuery("");
    inputRef.current?.focus();
  };

  return (
    <Box
      data-search-bar
      sx={{
        flexGrow: 1,
        minWidth: 0,
        maxWidth: fullWidth ? "100%" : "800px",
        width: "100%",
        mx: "auto",
        position: "relative",
      }}
    >
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity="warning"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Paper
        component="form"
        onSubmit={handleSearchSubmit}
        elevation={isActive ? 4 : 0}
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          borderRadius: "12px",
          border: "2px solid",
          borderColor: isActive ? navTokens.primary : "transparent",
          backgroundColor: isActive ? navTokens.bg : navTokens.bgMuted,
          boxShadow: isActive ? `0 0 0 4px ${navTokens.focusRing}` : "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          height: isExplicitMobile ? 44 : isCompact ? 46 : 52,
          position: "relative",
          zIndex: isActive ? 20 : 1,
        }}
      >
        {/* Retailer Selector */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            borderRight: `1px solid ${navTokens.border}`,
            bgcolor: isActive ? navTokens.bg : "transparent",
            borderTopLeftRadius: "10px",
            borderBottomLeftRadius: "10px",
            transition: "background-color 0.2s",
          }}
        >
          <Select
            value={selectedRetailer}
            onChange={handleRetailerChange}
            onOpen={() => setIsMenuOpen(true)}
            onClose={() => setIsMenuOpen(false)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            disableUnderline
            variant="standard"
            IconComponent={KeyboardArrowDownIcon}
            MenuProps={{
              disableScrollLock: true,
              transformOrigin: { vertical: "top", horizontal: "left" },
              PaperProps: {
                elevation: 0,
                sx: {
                  mt: 1,
                  borderRadius: "12px",
                  border: `1px solid ${navTokens.border}`,
                  boxShadow: navTokens.shadow,
                  minWidth: 180,
                  overflow: "visible",
                  "& .MuiList-root": { p: 1 },
                  "&:before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    left: 20,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                    borderLeft: `1px solid ${navTokens.border}`,
                    borderTop: `1px solid ${navTokens.border}`,
                  },
                },
              },
            }}
            sx={{
              height: "100%",
              minWidth: isExplicitMobile ? 70 : { xs: 70, sm: 140 },
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                pl: isExplicitMobile ? 1.5 : 2,
                pr: "32px !important",
                height: "100%",
                "&:focus": { bgcolor: "transparent" },
              },
              "& .MuiSelect-icon": {
                display: "block",
                color: navTokens.textMuted,
                right: 4,
                fontSize: "20px",
              },
              "&:hover .MuiSelect-icon": {
                color: navTokens.primary,
              },
              "&.Mui-focused .MuiSelect-icon": {
                color: navTokens.primary,
              },
            }}
            renderValue={(value) => {
              const key = (value as RetailerKey) || "amazon";
              const info = RETAILERS[key];
              return (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    width: "100%",
                  }}
                >
                  <Box
                    component="img"
                    src={info.logo}
                    alt={info.label}
                    sx={{
                      height: 20,
                      width: "auto",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                  {!isExplicitMobile && (
                    <Box
                      component="span"
                      className="notranslate"
                      translate="no"
                      sx={{
                        display: { xs: "none", sm: "block" },
                        fontSize: "14px",
                        fontWeight: 600,
                        color: navTokens.text,
                      }}
                    >
                      {info.label}
                    </Box>
                  )}
                </Box>
              );
            }}
          >
            {Object.entries(RETAILERS).map(([key, info]) => {
              const isSelected = selectedRetailer === key;
              return (
                <MenuItem
                  key={key}
                  value={key}
                  disableRipple
                  sx={{
                    borderRadius: "8px",
                    mb: 0.5,
                    py: 1.25,
                    px: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transition: "all 0.2s",
                    bgcolor: isSelected
                      ? navTokens.menuSelectedBg
                      : "transparent",
                    color: isSelected ? navTokens.menuSelectedText : "#1F2937",
                    "&:hover": {
                      bgcolor: isSelected
                        ? navTokens.menuSelectedBg
                        : navTokens.menuHover,
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      component="img"
                      src={info.logo}
                      alt={info.label}
                      sx={{ height: 20, width: 20, objectFit: "contain" }}
                    />
                    <Box
                      component="span"
                      sx={{
                        fontSize: "14px",
                        fontWeight: isSelected ? 600 : 500,
                      }}
                    >
                      {info.label}
                    </Box>
                  </Box>
                  {isSelected && (
                    <CheckIcon
                      sx={{ fontSize: 18, color: navTokens.primary }}
                    />
                  )}
                </MenuItem>
              );
            })}
          </Select>
        </Box>

        {/* Search Input */}
        <InputBase
          inputRef={inputRef}
          placeholder={
            selectedRetailer === "amazon"
              ? myTranslate("navbar.searchPlaceholderAmazon") ||
                "Search Amazon..."
              : selectedRetailer === "walmart"
                ? myTranslate("navbar.searchPlaceholderWalmart") ||
                  `Search ${retailerLabel}...`
                : myTranslate("navbar.searchPlaceholder", {
                    retailer: retailerLabel,
                  }) || `Search ${retailerLabel}...`
          }
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(event) => {
            if (event.key === "Escape" && searchQuery) {
              handleClear();
            }
          }}
          inputProps={{
            "aria-label": "search products",
            inputMode: "search",
            autoCapitalize: "none",
            autoCorrect: "off",
            className: "notranslate",
            translate: "no",
          }}
          sx={{
            ml: 2,
            flex: 1,
            fontSize: isExplicitMobile ? "15px" : "16px",
            color: navTokens.text,
            fontWeight: 400,
            "& input::placeholder": {
              color: navTokens.textSubtle,
              opacity: 1,
            },
          }}
        />

        {/* Clear Button */}
        <Fade in={!!searchQuery}>
          <IconButton
            size="small"
            onClick={handleClear}
            sx={{
              mr: 1,
              p: 0.5,
              color: navTokens.textSubtle,
              bgcolor: navTokens.bgMuted,
              "&:hover": { color: navTokens.textMuted, bgcolor: "#E5E7EB" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Fade>

        {/* Search Button */}
        <Box
          component="button"
          type="submit"
          disabled={!trimmedQuery}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: isExplicitMobile ? 48 : { xs: 50, sm: 60 },
            border: "none",
            cursor: trimmedQuery ? "pointer" : "default",
            bgcolor: trimmedQuery ? navTokens.primary : "transparent",
            color: trimmedQuery ? navTokens.text : navTokens.textSubtle,
            borderLeft: `1px solid ${navTokens.border}`,
            borderTopRightRadius: "10px",
            borderBottomRightRadius: "10px",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: trimmedQuery ? navTokens.primaryHover : "transparent",
            },
            ...(trimmedQuery && {
              "@media (max-width: 600px)": {
                bgcolor: "transparent",
                color: navTokens.primary,
                borderLeft: "none",
              },
            }),
          }}
        >
          <SearchIcon sx={{ fontSize: isExplicitMobile ? 22 : 24 }} />
        </Box>
      </Paper>
    </Box>
  );
};

export default SearchBar;
